import {
  apiPost,
  apiFetch,
  getRefreshToken,
  setAuthTokens,
  removeAuthToken,
} from '@/utils/fetchWrapper';
import {
  LoginRequest,
  LoginResponse,
  TwoFactorVerifyRequest,
  TwoFactorVerifyResponse,
  User,
  AuthPasswordChangeRequest,
  MfaMethodConfig,
  MfaCreateMethodResponse,
  MfaConfirmMethodRequest,
} from '@/types/api';

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiPost<LoginResponse>('/auth/login/', credentials);
    if ('access' in response) {
      setAuthTokens(response.access, response.refresh);
    }
    return response;
  },

  async verify2fa(payload: TwoFactorVerifyRequest): Promise<void> {
    const response = await apiPost<TwoFactorVerifyResponse>('/auth/login/verify/', payload);
    setAuthTokens(response.access, response.refresh);
  },

  async getMfaMethods(): Promise<MfaMethodConfig[]> {
    return apiFetch<MfaMethodConfig[]>('/auth/mfa/');
  },

  async createMfaMethod(method: string): Promise<MfaCreateMethodResponse> {
    return apiPost<MfaCreateMethodResponse>('/auth/mfa/', { method });
  },

  async confirmMfaMethod(payload: MfaConfirmMethodRequest): Promise<void> {
    await apiPost('/auth/mfa/confirm/', payload);
  },

  async getCurrentUser(): Promise<User> {
    const data = await apiFetch<User & { pk?: string }>('/auth/me/');
    return {
      ...data,
      id: data.id ?? data.pk ?? '',
    };
  },

  async changePassword(payload: AuthPasswordChangeRequest): Promise<void> {
    await apiPost('/auth/password/change/', payload);
  },

  async logout(): Promise<void> {
    const refresh = getRefreshToken();

    try {
      if (!refresh) {
        return;
      }

      await apiPost('/auth/logout/', { refresh });
    } catch {
      // Backend may be unreachable; tokens are cleared in finally regardless.
    } finally {
      removeAuthToken();
    }
  },
};
